import { useEffect, useRef, useState } from 'react';
import { assinarAtualizacao, haAtualizacao, aplicarAtualizacao } from './swRegistration';

/**
 * Ciclo de vida do app instalado: atualização e convite de instalação.
 *
 * Duas coisas que não são óbvias e custam caro se ficarem implícitas:
 *
 * 1. Atualização é PERGUNTA, não imposição. Trocar o app por baixo de quem está
 *    escrevendo a descrição de uma tarefa perde texto não salvo. Por isso o
 *    plugin está em `registerType: 'prompt'` e quem chama `updateApp()` é o
 *    usuário.
 *
 * 2. No iPhone NÃO existe `beforeinstallprompt`. A Apple nunca implementou, e
 *    instalar lá é o usuário ir no menu compartilhar e escolher "Adicionar à
 *    Tela de Início". Então `canInstall` é sempre falso no iOS, e `isIOS` existe
 *    para a UI poder ensinar o caminho em vez de mostrar um botão que não
 *    funciona.
 */

interface EventoDeInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PwaLifecycle {
  /** Há versão nova baixada, esperando o usuário aceitar recarregar. */
  updateReady: boolean;
  /** Recarrega no código novo. Só faz sentido com `updateReady`. */
  updateApp: () => void;
  /** O navegador ofereceu instalação (Android/Chrome desktop). */
  canInstall: boolean;
  /** Dispara o diálogo nativo de instalação. */
  install: () => Promise<void>;
  /** Já está rodando instalado, fora do navegador. */
  isStandalone: boolean;
  isIOS: boolean;
}

const detectaStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  // `display-mode: standalone` cobre Android e desktop; `navigator.standalone`
  // é o jeito antigo e é o ÚNICO que funciona no Safari do iPhone.
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true;
};

const detectaIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
    // iPad moderno se anuncia como Mac; o toque é o que o denuncia.
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export function usePwaLifecycle(): PwaLifecycle {
  const [updateReady, setUpdateReady] = useState(() => haAtualizacao());
  const [canInstall, setCanInstall] = useState(false);
  const eventoInstalacaoRef = useRef<EventoDeInstalacao | null>(null);

  // Quem registra o service worker e o main.tsx, no arranque. Este hook so
  // escuta: registrar aqui faria o registro depender de um componente estar
  // montado, e foi exatamente esse o defeito da primeira versao (visitante
  // deslogado nunca registrava nada).
  useEffect(() => assinarAtualizacao(() => setUpdateReady(true)), []);

  useEffect(() => {
    const aoOferecer = (e: Event) => {
      // Sem o preventDefault, o Chrome mostra a barra dele por cima do app, e
      // o convite passa a competir com o nosso em vez de ser o nosso.
      e.preventDefault();
      eventoInstalacaoRef.current = e as EventoDeInstalacao;
      setCanInstall(true);
    };
    const aoInstalar = () => {
      eventoInstalacaoRef.current = null;
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', aoOferecer);
    window.addEventListener('appinstalled', aoInstalar);
    return () => {
      window.removeEventListener('beforeinstallprompt', aoOferecer);
      window.removeEventListener('appinstalled', aoInstalar);
    };
  }, []);

  return {
    updateReady,
    updateApp: () => aplicarAtualizacao(),
    canInstall,
    install: async () => {
      const evento = eventoInstalacaoRef.current;
      if (!evento) return;
      await evento.prompt();
      await evento.userChoice;
      // O evento é de uso único: guardá-lo para uma segunda tentativa faz o
      // navegador ignorar a chamada em silêncio.
      eventoInstalacaoRef.current = null;
      setCanInstall(false);
    },
    isStandalone: detectaStandalone(),
    isIOS: detectaIOS(),
  };
}
